// @ts-check
import classNames from 'classnames';
import { graphql, navigate } from 'gatsby';
import React from 'react';
import Card from '~/components/Common/Card';
import Link from '~/components/Common/Link';
import LoginFormBusinessBanking from '~/components/Common/LoginFormBusinessBanking';
import LoginFormBusinessQ2Banking from '~/components/Common/LoginFormBusinessQ2Banking';
import LoginFormPersonalBanking from '~/components/Common/LoginFormPersonalBanking';
import LoginCardMain, { Head as LoginCardHead } from '~/components/Modules/LoginCardMain';

export const query = graphql`
	fragment PageHeroLoginFormFragment on Ozk_page_heroCover_content {
		login_form {
			heading
			username_placeholder
			password_placeholder
			button_text
			which
			centered
			links {
				link_text
				link_url
			}
			bottom_icons {
				link {
					title
					href
				}
				icon_name
			}
		}
	}
`;

/**
 * @typedef PageHeroLoginFormOptions
 * @property {string} uid Unused and meant for typing. Remove when you can.
 * @property {'personal' | 'business' | 'business-q2' | 'none'} which The type of login form requested.
 * @property {boolean} centered A value indicating if this form should be the main centered content of the hero.
 * @property {string} heading The heading text of the login form.
 * @property {string} username_placeholder The placeholder text for the username field.
 * @property {string} password_placeholder The placeholder text for the password field.
 * @property {string} button_text The text for the form button.
 * @property {PageHeroLoginLink[]} links The text to display on the login button
 * @property {BottomIcons[]} bottom_icons The text to display on the login button
 */
/**
 * @typedef PageHeroLoginLink
 * @property {string} link_text The display text of the link
 * @property {string} link_url The url of the link
 */
/**
 * @typedef BottomIcons
 * @property {string} icon_name Name of icon that needs to be used
 * @property {import('../CSTypes').CSLink} link The array containing href and title for above icon_name
 */
/** @typedef {import('~/contentstack/types').ModelWithLivePreviewAttr<PageHeroLoginFormOptions>} PageHeroLoginFormOptionsWith$ */
/**
 * @typedef PageHeroLoginFormProps
 * @property {PageHeroLoginFormOptionsWith$} [options]
 */
/** @type {import('~/types/React').ReactFC<PageHeroLoginFormProps>} */
const PageHeroLoginForm = ({ options }) => {
	if (!options) {
		return null;
	}

	return (
		<div className={classNames('page-hero-login-form', options.which, { centered: options.centered })}>
			{!options.centered && options.which !== 'none' ? (
				<LoginCardMain options={options} />
			) : (
				<LoginCardSimple options={options} />
			)}
		</div>
	);
};

/** @type {import('~/types/React').ReactFC<{ options: PageHeroLoginFormOptions }>} */
const LoginCardSimple = ({ options }) => (
	<Card className="login-card-simple" onExit={options.which === 'none' ? undefined : () => navigate('/login')}>
		{options.which === 'none' ? <LoginCardSimpleLinks options={options} /> : <LoginCardSimpleForm options={options} />}
	</Card>
);

/** @type {import('~/types/React').ReactFC<{ options: PageHeroLoginFormOptionsWith$ }>} */
const LoginCardSimpleForm = ({ options }) => (
	<React.Fragment>
		<h3 className="heading" {...options.$?.heading}>
			{options.heading}
		</h3>
		<div className="simple">
			{options.which === 'business' && <LoginFormBusinessBanking options={options} />}
			{options.which === 'business-q2' && <LoginFormBusinessQ2Banking options={options} />}
			{options.which === 'personal' && <LoginFormPersonalBanking options={options} />}
		</div>
	</React.Fragment>
);

/** @type {import('~/types/React').ReactFC<{ options: PageHeroLoginFormOptionsWith$ }>} */
const LoginCardSimpleLinks = ({ options }) => (
	<div>
		<h3 className="heading" {...options.$?.heading}>
			{options.heading}
		</h3>
		<div className="login-nav" {...options.$?.which}>
			<Link to="/login/personal" id="Personal_Banking_Login">
				<i aria-hidden="true" className="icon">
					person
				</i>
				<span className="login-nav-text">Personal Banking</span>
			</Link>
			<Link to="/login/business" id="Business_Banking_Login">
				<i aria-hidden="true" className="icon">
					business
				</i>
				<span className="login-nav-text">Business Banking</span>
			</Link>
			<a href="https://www.firstbankcard.com/" aria-label="Login to Credt Cards">
				<i aria-hidden="true" className="icon">
					credit_card
				</i>
				<span className="login-nav-text">Credit Cards</span>
			</a>
			<a
				href="https://clientpoint.fisglobal.com/tdcb/main/UserLogon?bankNumber=8B&amp;subProduct"
				aria-label="Wealth Client Point"
			>
				<i aria-hidden="true" className="icon">
					attach_money
				</i>
				<span className="login-nav-text">Wealth Client Point</span>
			</a>
			<a href="https://ozk.iremitweb.com/" aria-label="Lockbox Access">
				<i aria-hidden="true" className="icon">
					lock
				</i>
				<span className="login-nav-text">Lockbox Access</span>
			</a>
			<Link to="/personal/lending/" aria-label="Loan Payments">
				<i aria-hidden="true" className="icon">
					directions_car
				</i>
				<span className="login-nav-text">Loan Payments</span>
			</Link>
		</div>
	</div>
);

export default PageHeroLoginForm;
export const Head = LoginCardHead;
